import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient, currentTenantId, fetchMyTenants, renameTenant } from '../api.js';
import { SaveIcon } from '../components/icons';

/**
 * Tenant-level settings — today just rename.
 *
 * Lives under /settings so there's a clear home when more
 * tenant-wide knobs appear (dashboard_url, default locale, JWKS,
 * etc).  Owner-only for writes; the caller's role comes from the
 * /v1/platform/tenants response which every Shell already fetches.
 */
export function TenantSettings() {
  const { getToken } = useAuth();
  const api = useMemo(() => createApiClient(getToken), [getToken]);
  const tenantId = currentTenantId();

  const [role, setRole] = useState(null);
  const [currentName, setCurrentName] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [notice, setNotice] = useState(null);
  const [dashboardUrl, setDashboardUrl] = useState('');

  useEffect(() => {
    let alive = true;
    fetchMyTenants(getToken)
      .then((resp) => {
        if (!alive) return;
        const list = Array.isArray(resp) ? resp : (resp?.tenants || []);
        const mine = list.find((t) => t.tenant_id === tenantId);
        setRole(mine?.role || null);
        setCurrentName(mine?.name || tenantId);
        setName(mine?.name || tenantId);
      })
      .catch(() => { /* non-fatal */ });
    api('/v1/admin/config')
      .then((cfg) => { if (alive) setDashboardUrl(cfg?.dashboard_url || ''); })
      .catch(() => { /* optional */ });
    return () => { alive = false; };
  }, [getToken, api, tenantId]);

  const canEdit = role === 'owner';

  async function save(e) {
    e.preventDefault();
    if (!canEdit) return;
    setBusy(true);
    setErr(null);
    setNotice(null);
    try {
      const updated = await renameTenant(getToken, tenantId, name.trim());
      setCurrentName(updated?.name || name.trim());
      setNotice('Saved. The tenant switcher refreshes on next page load.');
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <div className="page__title">
        <div>
          <h1>Tenant settings</h1>
          <p className="page__subtitle">
            Tenant-wide details. Rename is owner-only; everything else
            is informational here for now — JWKS, sites, and analytics
            providers live on their dedicated pages.
          </p>
        </div>
      </div>

      <section className="card">
        <h3 className="card__title">Display name</h3>
        <p className="card__body">
          Shown in the sidebar switcher and the SDK overlay's
          link-outs. Tenant id (<code>{tenantId}</code>) is the
          internal handle — it never changes.
        </p>
        <form
          onSubmit={save}
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr auto',
            gap: 12,
            alignItems: 'end',
            marginTop: 10,
          }}
        >
          <label className="field">
            <span className="field__label">Name</span>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!canEdit}
              placeholder="Acme Inc"
            />
          </label>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={!canEdit || busy || name.trim() === currentName}
          >
            <SaveIcon /> {busy ? 'Saving…' : 'Save'}
          </button>
        </form>
        {!canEdit && (
          <p className="list__meta" style={{ marginTop: 8 }}>
            Only owners can rename the tenant. Your role: <strong>{role || 'unknown'}</strong>.
          </p>
        )}
        {err && <p style={{ color: 'var(--danger-700)', marginTop: 8 }}>{err}</p>}
        {notice && <p style={{ color: 'var(--success-700)', marginTop: 8 }}>{notice}</p>}
      </section>

      <section className="card">
        <h3 className="card__title">Quick facts</h3>
        <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 14px', fontSize: 13, margin: '8px 0 0' }}>
          <dt style={{ color: 'var(--text-3)' }}>Tenant id</dt>
          <dd style={{ margin: 0 }}><code>{tenantId}</code></dd>
          <dt style={{ color: 'var(--text-3)' }}>Your role</dt>
          <dd style={{ margin: 0 }}>{role || '—'}</dd>
          <dt style={{ color: 'var(--text-3)' }}>Dashboard URL</dt>
          <dd style={{ margin: 0 }}>{dashboardUrl ? <code>{dashboardUrl}</code> : <em>(not set — the overlay's "More in dashboard" link falls back to the API host)</em>}</dd>
        </dl>
      </section>
    </div>
  );
}
